import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Lang = "es" | "en" | "fr";

type Dict = {
  toggleAria: string;

  landingDescription: string;

  loading: string;
  linkNotFound: string;
  linkNotFoundDesc: string;
  somethingWentWrong: string;
  tryAgain: string;

  greetingActive: (name: string) => string;
  greetingScheduled: (name: string) => string;
  greetingExpired: (name: string) => string;
  greetingRevoked: (name: string) => string;
  checkoutLabel: string;
  accessOpensIn: (countdown: string) => string;
  accessOpensInLabel: string;
  opensAtLabel: string;
  unitDays: string;
  unitHours: string;
  unitMinutes: string;
  unitSeconds: string;
  linkRevoked: string;
  linkExpired: string;
  linkNotYetActive: string;
  linkExpiredTitle: string;
  linkExpiredDesc: string;
  linkRevokedTitle: string;
  linkRevokedDesc: string;

  graceTitle: string;
  graceDesc: string;
  graceEndsAtLabel: string;

  openPortalDoor: string;
  openApartmentDoor: string;
  opening: string;
  closePortalReminder: string;
  closePortalWarning: string;

  confirmOpenPortalTitle: string;
  confirmOpenApartmentTitle: string;
  confirmOpenPortalDesc: string;
  confirmApartmentWarning: string;
  confirmOpenApartmentDesc: string;
  confirm: string;
  cancel: string;

  portalOpened: string;
  apartmentOpened: string;
  couldNotOpenDoor: string;
  doorNotResponded: string;

  feedbackPromptTitle: string;
  feedbackPromptDesc: string;
  feedbackRatingSadLabel: string;
  feedbackRatingNeutralLabel: string;
  feedbackRatingHappyLabel: string;
  feedbackCommentLabel: string;
  feedbackCommentPlaceholder: string;
  feedbackSubmit: string;
  feedbackSubmitting: string;
  feedbackThanksTitle: string;
  feedbackThanksDescSad: string;
  feedbackThanksDescNeutral: string;
  feedbackHappyTitle: string;
  feedbackHappyDesc: string;
  feedbackBookingButton: string;
  feedbackBookingThanks: string;
  feedbackChangeRating: string;
  feedbackSaveError: string;

  gettingHereTitle: string;
  gettingHereOpenInMaps: string;
  gettingHereApartmentLabel: string;
  gettingHereElevatorLabel: string;
  gettingHereTip: (floor: string, apartment: string) => string;

  // Registration wizard
  regGateTitle: string;
  regGateDesc: (count: number) => string;
  regWizardStart: string;
  regStepBooker: string;
  regStepGuests: string;
  regStepGuestN: (n: number, total: number) => string;
  regProgressLabel: (done: number, total: number) => string;
  regBookerTitle: string;
  regBookerDesc: string;
  regBookerSaving: string;
  regBookerSave: string;
  regBookerEditNote: string;
  regGuestsTitle: string;
  regGuestsDesc: (count: number) => string;
  regSlotNumber: (n: number) => string;
  regSlotEmpty: string;
  regSlotComplete: string;
  regSlotFill: string;
  regSlotEdit: string;
  regSlotChooseMode: string;
  regSlotModeSelf: string;
  regSlotModeSameAsBooker: (name: string) => string;
  regSlotSameAsBookerLocked: string;
  regSlotSameAsBookerEditPrompt: string;
  regSlotGoToBooker: string;
  regSlotClear: string;
  regSlotClearConfirmTitle: string;
  regSlotClearConfirmDesc: string;
  regSlotSave: string;
  regSlotSaving: string;
  regSlotCancel: string;
  regBookerAlreadyClaimedBy: (slot: number) => string;
  regSlotAutoSwitched: (from: number, to: number) => string;
  regSlotAutoSwitchedAllFull: (slot: number) => string;
  regErrorTitle: string;
  regErrorGeneric: string;
  regCompletedTitle: string;
  regCompletedDesc: string;
  regLockedDesc: string;
  regEditMyData: string;
  regGuestEditLocked: string;
  regOpenAfterRegistration: string;
  regBookingReferenceShort: string;

  // Person form field labels
  fieldNombre: string;
  fieldPrimerApellido: string;
  fieldSegundoApellido: string;
  fieldSegundoApellidoHint: string;
  fieldFechaNacimiento: string;
  fieldNacionalidad: string;
  fieldNacionalidadSearch: string;
  fieldNacionalidadNoResults: string;
  fieldSexo: string;
  sexoHombre: string;
  sexoMujer: string;
  sexoOtro: string;
  fieldDocumentoTipo: string;
  docTipoNIF: string;
  docTipoNIE: string;
  docTipoPasaporte: string;
  docTipoOtro: string;
  fieldDocumentoNumero: string;
  docScanCta: string;
  docScanHint: string;
  docScanProcessing: string;
  docScanSuccess: string;
  docScanRetake: string;
  docScanRemove: string;
  docScanPrivacy: string;
  docScanDiscarded: string;
  docScanAiBadge: string;
  docScanAiTooltip: string;
  docScanErrorNoDocument: string;
  docScanErrorUpstream: string;
  personalSectionLabel: string;
  reviewAiPrefillTitle: string;
  reviewAiPrefillDesc: string;
  docEnterManually: string;
  docHideManual: string;
  docSectionLabel: string;
  fieldContacto: string;
  fieldContactoHint: string;
  fieldTelefono: string;
  fieldCorreo: string;

  // Validation messages (used as form errors)
  errRequired: string;
  errTooLong: string;
  errInvalidDate: string;
  errInvalidCountry: string;
  errInvalidNIF: string;
  errInvalidNIE: string;
  errInvalidPasaporte: string;
  errInvalidDocFormat: string;
  errInvalidPhone: string;
  errInvalidEmail: string;
  errContactRequired: string;
  errBookerMissing: string;
  errBookerAlreadyClaimed: string;
  errStayStarted: string;
  errNotRegistered: string;

  // Admin
  adminStayGuestCountLabel: string;
  adminStayGuestCountHelp: string;
  adminBookingReferenceLabel: string;
  adminBookingReferenceHelp: string;
  adminRegistrationTitle: string;
  adminRegistrationBooker: string;
  adminRegistrationGuest: (n: number) => string;
  adminRegistrationEmptySlot: string;
  adminRegistrationLastUpdated: (when: string) => string;
  adminRegistrationEdit: string;
  adminRegistrationClear: string;
  adminRegistrationStatusComplete: string;
  adminRegistrationStatusIncomplete: (filled: number, total: number) => string;
  adminRegistrationMirror: string;
  adminRegistrationGuestCount: (count: number) => string;
  adminRegistrationBookingReference: string;
  adminRegistrationNoBookingReference: string;
};

const dictionaries: Record<Lang, Dict> = {
  es: {
    toggleAria: "Cambiar idioma",
    landingDescription:
      "Portal de acceso privado. Si vienes como huésped, tu anfitrión te habrá enviado un enlace personal.",
    loading: "Cargando...",
    linkNotFound: "Enlace no encontrado",
    linkNotFoundDesc: "Este enlace de acceso no existe.",
    somethingWentWrong: "Algo ha salido mal",
    tryAgain: "Inténtalo de nuevo en un momento.",
    greetingActive: (name) => `¡Bienvenido, ${name}!`,
    greetingScheduled: (name) => `¡Te esperamos, ${name}!`,
    greetingExpired: (name) => `¡Gracias por tu visita, ${name}!`,
    greetingRevoked: (name) => `Hola, ${name}`,
    checkoutLabel: "Check-out",
    accessOpensIn: (c) => `El acceso comienza en ${c}.`,
    accessOpensInLabel: "El acceso comienza en",
    opensAtLabel: "Comienza",
    unitDays: "días",
    unitHours: "horas",
    unitMinutes: "min",
    unitSeconds: "seg",
    linkRevoked: "Este enlace ha sido revocado.",
    linkExpired: "Este enlace ha caducado.",
    linkNotYetActive: "Este enlace aún no está activo.",
    linkExpiredTitle: "Acceso finalizado",
    linkExpiredDesc: "Tu estancia ya ha terminado.",
    linkRevokedTitle: "Acceso revocado",
    linkRevokedDesc: "Contacta con tu anfitrión si necesitas ayuda.",
    graceTitle: "Tiempo adicional",
    graceDesc:
      "Tu check-out ha pasado. Aún puedes abrir las puertas durante unos minutos para recoger tus cosas.",
    graceEndsAtLabel: "Cierre definitivo",
    openPortalDoor: "Abrir portal",
    openApartmentDoor: "Abrir apartamento",
    opening: "Abriendo...",
    closePortalReminder:
      "Asegúrate de cerrar la puerta del portal al salir.",
    closePortalWarning: "Por favor, cierra la puerta al salir.",
    confirmOpenPortalTitle: "¿Abrir la puerta del portal?",
    confirmOpenApartmentTitle: "¿Abrir la puerta del apartamento?",
    confirmOpenPortalDesc: "La puerta se abrirá durante unos segundos.",
    confirmApartmentWarning: "Hay que cerrarla a mano",
    confirmOpenApartmentDesc:
      "Una vez abierta, no se puede cerrar en remoto; alguien tiene que empujarla para cerrarla.",
    confirm: "Abrir",
    cancel: "Cancelar",
    portalOpened: "Portal abierto",
    apartmentOpened: "Puerta del apartamento abierta",
    couldNotOpenDoor: "No se pudo abrir la puerta",
    doorNotResponded: "La puerta no respondió.",

    feedbackPromptTitle: "¿Qué tal tu estancia?",
    feedbackPromptDesc: "Nos encantaría saber cómo ha ido.",
    feedbackRatingSadLabel: "Mejorable",
    feedbackRatingNeutralLabel: "Aceptable",
    feedbackRatingHappyLabel: "Genial",
    feedbackCommentLabel: "¿Qué podríamos mejorar?",
    feedbackCommentPlaceholder: "Cuéntanoslo (opcional)",
    feedbackSubmit: "Enviar",
    feedbackSubmitting: "Enviando...",
    feedbackThanksTitle: "¡Gracias por tu opinión!",
    feedbackThanksDescSad:
      "Sentimos que no haya sido perfecto. Lo tendremos muy en cuenta.",
    feedbackThanksDescNeutral:
      "Gracias por contárnoslo, lo tendremos en cuenta.",
    feedbackHappyTitle: "¡Nos alegra muchísimo!",
    feedbackHappyDesc:
      "Ponemos mucho cariño en este lugar. Una reseña en Booking nos haría muchísima ilusión.",
    feedbackBookingButton: "Dejar una reseña en Booking",
    feedbackBookingThanks: "¡Muchas gracias por tu tiempo!",
    feedbackChangeRating: "Cambiar mi opinión",
    feedbackSaveError: "No hemos podido guardar tu opinión. Inténtalo de nuevo.",

    gettingHereTitle: "Cómo llegar",
    gettingHereOpenInMaps: "Abrir en Maps",
    gettingHereApartmentLabel: "Apartamento",
    gettingHereElevatorLabel: "Ascensor",
    gettingHereTip: (floor, apartment) =>
      `En el ascensor pulsa el ${floor}, aunque el apartamento sea el ${apartment}.`,

    regGateTitle: "Antes de empezar",
    regGateDesc: (count) =>
      count === 1
        ? "Por ley necesitamos los datos del reservante y de la persona que se aloja. No te llevará más de un par de minutos."
        : `Por ley necesitamos los datos del reservante y de las ${count} personas que se alojan. No te llevará más de un par de minutos.`,
    regWizardStart: "Empezar",
    regStepBooker: "Reservante",
    regStepGuests: "Huéspedes",
    regStepGuestN: (n, total) => `Huésped ${n} de ${total}`,
    regProgressLabel: (done, total) => `${done} de ${total} completados`,
    regBookerTitle: "Datos del reservante",
    regBookerDesc:
      "Persona que hizo la reserva. Si también se aloja, podrás reutilizar estos datos en el siguiente paso.",
    regBookerSaving: "Guardando...",
    regBookerSave: "Guardar y continuar",
    regBookerEditNote:
      "Los datos están guardados. Puedes editarlos hasta el inicio de la estancia.",
    regGuestsTitle: "Personas que se alojan",
    regGuestsDesc: (count) =>
      count === 1
        ? "Necesitamos los datos de la persona que va a alojarse."
        : `Necesitamos los datos de cada una de las ${count} personas que se alojan.`,
    regSlotNumber: (n) => `Huésped ${n}`,
    regSlotEmpty: "Sin completar",
    regSlotComplete: "Completado",
    regSlotFill: "Completar",
    regSlotEdit: "Editar",
    regSlotChooseMode: "¿Quién es este huésped?",
    regSlotModeSelf: "Otra persona",
    regSlotModeSameAsBooker: (name) => `Es ${name} (la persona que reservó)`,
    regSlotSameAsBookerLocked:
      "Estos datos vienen del reservante y no se pueden editar aquí.",
    regSlotSameAsBookerEditPrompt:
      "Para modificarlos, edita los datos del reservante.",
    regSlotGoToBooker: "Editar al reservante",
    regSlotClear: "Eliminar",
    regSlotClearConfirmTitle: "¿Eliminar este huésped?",
    regSlotClearConfirmDesc:
      "Se borrarán todos los datos de este huésped. No se puede deshacer.",
    regSlotSave: "Guardar",
    regSlotSaving: "Guardando...",
    regSlotCancel: "Cancelar",
    regBookerAlreadyClaimedBy: (slot) =>
      `El reservante ya está asignado al huésped ${slot}.`,
    regSlotAutoSwitched: (from, to) =>
      `Alguien acaba de completar el huésped ${from}. Te llevamos al huésped ${to}.`,
    regSlotAutoSwitchedAllFull: (slot) =>
      `Alguien acaba de completar el huésped ${slot}. Ya está todo listo.`,
    regErrorTitle: "Revisa los datos",
    regErrorGeneric: "No se ha podido guardar. Inténtalo de nuevo.",
    regCompletedTitle: "¡Registro completo!",
    regCompletedDesc:
      "Gracias. Ya está todo listo. Verás los botones de apertura cuando empiece tu estancia.",
    regLockedDesc:
      "Tus datos han sido enviados al registro obligatorio. Si necesitas modificar algo, contacta con el anfitrión.",
    regEditMyData: "Editar mis datos",
    regGuestEditLocked:
      "La estancia ya ha comenzado. Si necesitas modificar algo, contacta con el anfitrión.",
    regOpenAfterRegistration:
      "Por favor, completa el registro para poder abrir las puertas.",
    regBookingReferenceShort: "Referencia",

    fieldNombre: "Nombre",
    fieldPrimerApellido: "Primer apellido",
    fieldSegundoApellido: "Segundo apellido",
    fieldSegundoApellidoHint: "Opcional si no tienes",
    fieldFechaNacimiento: "Fecha de nacimiento",
    fieldNacionalidad: "Nacionalidad",
    fieldNacionalidadSearch: "Busca un país",
    fieldNacionalidadNoResults: "Sin resultados",
    fieldSexo: "Sexo",
    sexoHombre: "Hombre",
    sexoMujer: "Mujer",
    sexoOtro: "Otro",
    fieldDocumentoTipo: "Tipo de documento",
    docTipoNIF: "DNI / NIF",
    docTipoNIE: "NIE",
    docTipoPasaporte: "Pasaporte",
    docTipoOtro: "Otro documento extranjero",
    fieldDocumentoNumero: "Número de documento",
    docScanCta: "Escanear documento",
    docScanHint: "Hazle una foto y rellenamos los datos por ti.",
    docScanProcessing: "Leyendo el documento...",
    docScanSuccess: "Datos extraídos. Revísalos por si hay que corregir algo.",
    docScanRetake: "Cambiar foto",
    docScanRemove: "Quitar foto",
    docScanPrivacy:
      "Solo usamos la foto para rellenar los datos. No se guarda en ningún sitio.",
    docScanDiscarded: "Foto descartada. Solo conservamos los datos.",
    docScanAiBadge: "IA",
    docScanAiTooltip: "Procesado con IA",
    docScanErrorNoDocument:
      "No hemos reconocido un documento. Inténtalo con otra foto o introdúcelo a mano.",
    docScanErrorUpstream:
      "No hemos podido procesar la foto. Inténtalo de nuevo o introdúcelo a mano.",
    personalSectionLabel: "Datos personales",
    reviewAiPrefillTitle: "Revisa estos datos",
    reviewAiPrefillDesc:
      "Los hemos rellenado a partir de tu documento. Comprueba que estén correctos y modifica lo que haga falta.",
    docEnterManually: "Prefiero introducirlo a mano",
    docHideManual: "Volver a escanear",
    docSectionLabel: "Documento de identidad",
    fieldContacto: "Contacto",
    fieldContactoHint: "Indica al menos teléfono o correo.",
    fieldTelefono: "Teléfono",
    fieldCorreo: "Correo electrónico",

    errRequired: "Campo obligatorio",
    errTooLong: "Demasiado largo",
    errInvalidDate: "Fecha no válida",
    errInvalidCountry: "Elige un país de la lista",
    errInvalidNIF: "DNI/NIF no válido",
    errInvalidNIE: "NIE no válido",
    errInvalidPasaporte: "Pasaporte no válido",
    errInvalidDocFormat: "Documento no válido",
    errInvalidPhone: "Teléfono no válido",
    errInvalidEmail: "Correo no válido",
    errContactRequired: "Indica al menos un teléfono o un correo",
    errBookerMissing: "Primero completa los datos del reservante",
    errBookerAlreadyClaimed: "Otro huésped ya está asignado al reservante",
    errStayStarted: "La estancia ya ha comenzado",
    errNotRegistered: "Completa el registro para abrir las puertas",

    adminStayGuestCountLabel: "Número de huéspedes",
    adminStayGuestCountHelp: "Personas que se alojarán (1, 2 o 3).",
    adminBookingReferenceLabel: "Referencia",
    adminBookingReferenceHelp: "Número de reserva de Booking. No se muestra al huésped.",
    adminRegistrationTitle: "Registro de huéspedes",
    adminRegistrationBooker: "Reservante",
    adminRegistrationGuest: (n) => `Huésped ${n}`,
    adminRegistrationEmptySlot: "Sin completar",
    adminRegistrationLastUpdated: (when) => `Actualizado ${when}`,
    adminRegistrationEdit: "Editar",
    adminRegistrationClear: "Vaciar",
    adminRegistrationStatusComplete: "Registro completo",
    adminRegistrationStatusIncomplete: (filled, total) =>
      `${filled} de ${total} completados`,
    adminRegistrationMirror: "Mismos datos que el reservante",
    adminRegistrationGuestCount: (count) =>
      count === 1
        ? "1 huésped"
        : `${count} huéspedes`,
    adminRegistrationBookingReference: "Referencia",
    adminRegistrationNoBookingReference: "Sin referencia",
  },
  en: {
    toggleAria: "Change language",
    landingDescription:
      "Private access portal. If you are a guest, your host has shared a personal link with you.",
    loading: "Loading...",
    linkNotFound: "Link not found",
    linkNotFoundDesc: "This access link does not exist.",
    somethingWentWrong: "Something went wrong",
    tryAgain: "Try again in a moment.",
    greetingActive: (name) => `Welcome, ${name}!`,
    greetingScheduled: (name) => `See you soon, ${name}!`,
    greetingExpired: (name) => `Thank you for staying with us, ${name}!`,
    greetingRevoked: (name) => `Hello, ${name}`,
    checkoutLabel: "Check-out",
    accessOpensIn: (c) => `Access opens in ${c}.`,
    accessOpensInLabel: "Access opens in",
    opensAtLabel: "Opens",
    unitDays: "days",
    unitHours: "hours",
    unitMinutes: "min",
    unitSeconds: "sec",
    linkRevoked: "This link has been revoked.",
    linkExpired: "This link has expired.",
    linkNotYetActive: "This link is not active yet.",
    linkExpiredTitle: "Access ended",
    linkExpiredDesc: "Your stay has ended.",
    linkRevokedTitle: "Access revoked",
    linkRevokedDesc: "Please contact your host if you need help.",
    graceTitle: "Extra time",
    graceDesc:
      "Your check-out has passed. You can still open the doors for a few more minutes to grab your things.",
    graceEndsAtLabel: "Final lock-out",
    openPortalDoor: "Open portal",
    openApartmentDoor: "Open apartment",
    opening: "Opening...",
    closePortalReminder: "Please make sure the portal door is closed when you leave.",
    closePortalWarning: "Please close the door when you leave.",
    confirmOpenPortalTitle: "Open the portal door?",
    confirmOpenApartmentTitle: "Open the apartment door?",
    confirmOpenPortalDesc: "The door will open for a few seconds.",
    confirmApartmentWarning: "Needs to be closed manually",
    confirmOpenApartmentDesc:
      "Once opened, it can't be closed remotely; someone has to push it shut.",
    confirm: "Open",
    cancel: "Cancel",
    portalOpened: "Portal opened",
    apartmentOpened: "Apartment door opened",
    couldNotOpenDoor: "Could not open the door",
    doorNotResponded: "The door did not respond.",

    feedbackPromptTitle: "How was your stay?",
    feedbackPromptDesc: "We'd love to hear about it.",
    feedbackRatingSadLabel: "Could be better",
    feedbackRatingNeutralLabel: "It was OK",
    feedbackRatingHappyLabel: "Loved it",
    feedbackCommentLabel: "What could we improve?",
    feedbackCommentPlaceholder: "Tell us (optional)",
    feedbackSubmit: "Send",
    feedbackSubmitting: "Sending...",
    feedbackThanksTitle: "Thanks for sharing!",
    feedbackThanksDescSad:
      "We're sorry it wasn't perfect. We'll take this on board.",
    feedbackThanksDescNeutral:
      "Thanks for letting us know, we'll keep it in mind.",
    feedbackHappyTitle: "That makes our day!",
    feedbackHappyDesc:
      "We put our hearts into this place. A review on Booking would mean the world to us.",
    feedbackBookingButton: "Leave a review on Booking",
    feedbackBookingThanks: "Thank you so much!",
    feedbackChangeRating: "Change my rating",
    feedbackSaveError: "We couldn't save your feedback. Please try again.",

    gettingHereTitle: "Getting here",
    gettingHereOpenInMaps: "Open in Maps",
    gettingHereApartmentLabel: "Apartment",
    gettingHereElevatorLabel: "Elevator",
    gettingHereTip: (floor, apartment) =>
      `In the elevator press ${floor}, even though the apartment is ${apartment}.`,

    regGateTitle: "Before we start",
    regGateDesc: (count) =>
      count === 1
        ? "Spanish law requires us to register the booking contact and the person staying. It only takes a couple of minutes."
        : `Spanish law requires us to register the booking contact and the ${count} people staying. It only takes a couple of minutes.`,
    regWizardStart: "Start",
    regStepBooker: "Booking contact",
    regStepGuests: "Guests",
    regStepGuestN: (n, total) => `Guest ${n} of ${total}`,
    regProgressLabel: (done, total) => `${done} of ${total} done`,
    regBookerTitle: "Booking contact's details",
    regBookerDesc:
      "The person who made the booking. If they are also staying, you can reuse these details in the next step.",
    regBookerSaving: "Saving...",
    regBookerSave: "Save and continue",
    regBookerEditNote:
      "Saved. You can edit until the stay begins.",
    regGuestsTitle: "People staying",
    regGuestsDesc: (count) =>
      count === 1
        ? "We need details for the person who will stay."
        : `We need details for each of the ${count} people staying.`,
    regSlotNumber: (n) => `Guest ${n}`,
    regSlotEmpty: "Not filled",
    regSlotComplete: "Done",
    regSlotFill: "Fill in",
    regSlotEdit: "Edit",
    regSlotChooseMode: "Who is this guest?",
    regSlotModeSelf: "Someone else",
    regSlotModeSameAsBooker: (name) => `It's ${name} (the booking contact)`,
    regSlotSameAsBookerLocked:
      "These details come from the booking contact and can't be edited here.",
    regSlotSameAsBookerEditPrompt:
      "To change them, edit the booking contact's details.",
    regSlotGoToBooker: "Edit the booking contact",
    regSlotClear: "Delete",
    regSlotClearConfirmTitle: "Delete this guest?",
    regSlotClearConfirmDesc:
      "All the details for this guest will be removed. This can't be undone.",
    regSlotSave: "Save",
    regSlotSaving: "Saving...",
    regSlotCancel: "Cancel",
    regBookerAlreadyClaimedBy: (slot) =>
      `The booking contact is already assigned to guest ${slot}.`,
    regSlotAutoSwitched: (from, to) =>
      `Someone just filled guest ${from}. We've moved you to guest ${to}.`,
    regSlotAutoSwitchedAllFull: (slot) =>
      `Someone just filled guest ${slot}. Everyone is done.`,
    regErrorTitle: "Check the details",
    regErrorGeneric: "Couldn't save. Please try again.",
    regCompletedTitle: "Registration complete!",
    regCompletedDesc:
      "Thanks. You're all set. You'll see the door buttons once your stay starts.",
    regLockedDesc:
      "Your details have been sent to the mandatory registry. If you need to change anything, contact the host.",
    regEditMyData: "Edit my details",
    regGuestEditLocked:
      "Your stay has already started. Please contact the host if you need to change anything.",
    regOpenAfterRegistration:
      "Please finish the registration to be able to open the doors.",
    regBookingReferenceShort: "Reference",

    fieldNombre: "First name",
    fieldPrimerApellido: "First surname",
    fieldSegundoApellido: "Second surname",
    fieldSegundoApellidoHint: "Optional if you don't have one",
    fieldFechaNacimiento: "Date of birth",
    fieldNacionalidad: "Nationality",
    fieldNacionalidadSearch: "Search a country",
    fieldNacionalidadNoResults: "No results",
    fieldSexo: "Sex",
    sexoHombre: "Male",
    sexoMujer: "Female",
    sexoOtro: "Other",
    fieldDocumentoTipo: "Document type",
    docTipoNIF: "DNI / NIF",
    docTipoNIE: "NIE",
    docTipoPasaporte: "Passport",
    docTipoOtro: "Other foreign document",
    fieldDocumentoNumero: "Document number",
    docScanCta: "Scan document",
    docScanHint: "Take a photo and we'll fill in the details for you.",
    docScanProcessing: "Reading the document...",
    docScanSuccess: "Details extracted. Please review.",
    docScanRetake: "Change photo",
    docScanRemove: "Remove photo",
    docScanPrivacy:
      "The photo is only used to fill in the fields. We don't store it anywhere.",
    docScanDiscarded: "Photo discarded. We only keep the details.",
    docScanAiBadge: "AI",
    docScanAiTooltip: "Processed with AI",
    docScanErrorNoDocument:
      "We couldn't recognise a document. Try another photo or enter it by hand.",
    docScanErrorUpstream:
      "We couldn't process the photo. Please try again or enter it by hand.",
    personalSectionLabel: "Personal details",
    reviewAiPrefillTitle: "Please review",
    reviewAiPrefillDesc:
      "We filled these in from your document. Check that everything is correct and adjust whatever you need.",
    docEnterManually: "I'd rather type it in",
    docHideManual: "Scan instead",
    docSectionLabel: "Identity document",
    fieldContacto: "Contact",
    fieldContactoHint: "Provide at least a phone or an email.",
    fieldTelefono: "Phone",
    fieldCorreo: "Email",

    errRequired: "Required",
    errTooLong: "Too long",
    errInvalidDate: "Invalid date",
    errInvalidCountry: "Pick a country from the list",
    errInvalidNIF: "Invalid DNI/NIF",
    errInvalidNIE: "Invalid NIE",
    errInvalidPasaporte: "Invalid passport",
    errInvalidDocFormat: "Invalid document",
    errInvalidPhone: "Invalid phone",
    errInvalidEmail: "Invalid email",
    errContactRequired: "Provide at least a phone or an email",
    errBookerMissing: "Fill in the booking contact's details first",
    errBookerAlreadyClaimed: "Another guest is already set as the booking contact",
    errStayStarted: "The stay has already started",
    errNotRegistered: "Finish the registration to open the doors",

    adminStayGuestCountLabel: "Number of guests",
    adminStayGuestCountHelp: "People staying (1, 2 or 3).",
    adminBookingReferenceLabel: "Reference",
    adminBookingReferenceHelp: "Booking.com reservation number. Not shown to the guest.",
    adminRegistrationTitle: "Guest registration",
    adminRegistrationBooker: "Booking contact",
    adminRegistrationGuest: (n) => `Guest ${n}`,
    adminRegistrationEmptySlot: "Not filled",
    adminRegistrationLastUpdated: (when) => `Updated ${when}`,
    adminRegistrationEdit: "Edit",
    adminRegistrationClear: "Delete",
    adminRegistrationStatusComplete: "Registration complete",
    adminRegistrationStatusIncomplete: (filled, total) =>
      `${filled} of ${total} done`,
    adminRegistrationMirror: "Same details as the booking contact",
    adminRegistrationGuestCount: (count) =>
      count === 1 ? "1 guest" : `${count} guests`,
    adminRegistrationBookingReference: "Reference",
    adminRegistrationNoBookingReference: "No reference",
  },
  fr: {
    toggleAria: "Changer la langue",
    landingDescription:
      "Portail d'accès privé. Si vous êtes un voyageur, votre hôte vous a envoyé un lien personnel.",
    loading: "Chargement...",
    linkNotFound: "Lien introuvable",
    linkNotFoundDesc: "Ce lien d'accès n'existe pas.",
    somethingWentWrong: "Une erreur est survenue",
    tryAgain: "Réessayez dans un instant.",
    greetingActive: (name) => `Bienvenue, ${name} !`,
    greetingScheduled: (name) => `À très vite, ${name} !`,
    greetingExpired: (name) => `Merci pour votre visite, ${name} !`,
    greetingRevoked: (name) => `Bonjour, ${name}`,
    checkoutLabel: "Check-out",
    accessOpensIn: (c) => `L'accès s'ouvre dans ${c}.`,
    accessOpensInLabel: "L'accès s'ouvre dans",
    opensAtLabel: "S'ouvre",
    unitDays: "jours",
    unitHours: "heures",
    unitMinutes: "min",
    unitSeconds: "sec",
    linkRevoked: "Ce lien a été révoqué.",
    linkExpired: "Ce lien a expiré.",
    linkNotYetActive: "Ce lien n'est pas encore actif.",
    linkExpiredTitle: "Accès terminé",
    linkExpiredDesc: "Votre séjour est terminé.",
    linkRevokedTitle: "Accès révoqué",
    linkRevokedDesc: "Contactez votre hôte si vous avez besoin d'aide.",
    graceTitle: "Temps supplémentaire",
    graceDesc:
      "Votre check-out est passé. Vous pouvez encore ouvrir les portes quelques minutes pour récupérer vos affaires.",
    graceEndsAtLabel: "Verrouillage final",
    openPortalDoor: "Ouvrir le portail",
    openApartmentDoor: "Ouvrir l'appartement",
    opening: "Ouverture...",
    closePortalReminder:
      "Pensez à bien refermer le portail en sortant.",
    closePortalWarning: "Merci de bien refermer la porte en sortant.",
    confirmOpenPortalTitle: "Ouvrir la porte du portail ?",
    confirmOpenApartmentTitle: "Ouvrir la porte de l'appartement ?",
    confirmOpenPortalDesc: "La porte s'ouvrira pendant quelques secondes.",
    confirmApartmentWarning: "À refermer à la main",
    confirmOpenApartmentDesc:
      "Une fois ouverte, elle ne peut pas être refermée à distance ; quelqu'un doit la repousser.",
    confirm: "Ouvrir",
    cancel: "Annuler",
    portalOpened: "Portail ouvert",
    apartmentOpened: "Porte de l'appartement ouverte",
    couldNotOpenDoor: "Impossible d'ouvrir la porte",
    doorNotResponded: "La porte n'a pas répondu.",

    feedbackPromptTitle: "Comment s'est passé votre séjour ?",
    feedbackPromptDesc: "Nous serions ravis d'avoir votre avis.",
    feedbackRatingSadLabel: "À améliorer",
    feedbackRatingNeutralLabel: "Correct",
    feedbackRatingHappyLabel: "Génial",
    feedbackCommentLabel: "Que pourrions-nous améliorer ?",
    feedbackCommentPlaceholder: "Dites-le nous (facultatif)",
    feedbackSubmit: "Envoyer",
    feedbackSubmitting: "Envoi...",
    feedbackThanksTitle: "Merci pour votre retour !",
    feedbackThanksDescSad:
      "Nous sommes désolés que cela n'ait pas été parfait. Nous en tiendrons compte.",
    feedbackThanksDescNeutral:
      "Merci de nous l'avoir dit, nous le garderons en tête.",
    feedbackHappyTitle: "Vous nous faites plaisir !",
    feedbackHappyDesc:
      "Nous mettons tout notre cœur dans ce lieu. Un avis sur Booking nous ferait énormément plaisir.",
    feedbackBookingButton: "Laisser un avis sur Booking",
    feedbackBookingThanks: "Merci beaucoup !",
    feedbackChangeRating: "Modifier mon avis",
    feedbackSaveError:
      "Impossible d'enregistrer votre avis. Réessayez.",

    gettingHereTitle: "Comment venir",
    gettingHereOpenInMaps: "Ouvrir dans Maps",
    gettingHereApartmentLabel: "Appartement",
    gettingHereElevatorLabel: "Ascenseur",
    gettingHereTip: (floor, apartment) =>
      `Dans l'ascenseur, appuyez sur ${floor}, même si l'appartement est le ${apartment}.`,

    regGateTitle: "Avant de commencer",
    regGateDesc: (count) =>
      count === 1
        ? "La loi nous oblige à enregistrer le réservant et la personne qui séjourne. Cela ne prend que quelques minutes."
        : `La loi nous oblige à enregistrer le réservant et les ${count} personnes qui séjournent. Cela ne prend que quelques minutes.`,
    regWizardStart: "Commencer",
    regStepBooker: "Réservant",
    regStepGuests: "Voyageurs",
    regStepGuestN: (n, total) => `Voyageur ${n} sur ${total}`,
    regProgressLabel: (done, total) => `${done} sur ${total} complétés`,
    regBookerTitle: "Coordonnées du réservant",
    regBookerDesc:
      "Personne qui a effectué la réservation. Si elle séjourne aussi, vous pourrez réutiliser ces informations à l'étape suivante.",
    regBookerSaving: "Enregistrement...",
    regBookerSave: "Enregistrer et continuer",
    regBookerEditNote:
      "Les données sont enregistrées. Vous pouvez les modifier jusqu'au début du séjour.",
    regGuestsTitle: "Personnes qui séjournent",
    regGuestsDesc: (count) =>
      count === 1
        ? "Nous avons besoin des informations de la personne qui séjournera."
        : `Nous avons besoin des informations de chacune des ${count} personnes qui séjournent.`,
    regSlotNumber: (n) => `Voyageur ${n}`,
    regSlotEmpty: "À compléter",
    regSlotComplete: "Complété",
    regSlotFill: "Compléter",
    regSlotEdit: "Modifier",
    regSlotChooseMode: "Qui est ce voyageur ?",
    regSlotModeSelf: "Une autre personne",
    regSlotModeSameAsBooker: (name) =>
      `C'est ${name} (la personne qui a réservé)`,
    regSlotSameAsBookerLocked:
      "Ces données viennent du réservant et ne peuvent pas être modifiées ici.",
    regSlotSameAsBookerEditPrompt:
      "Pour les modifier, éditez les informations du réservant.",
    regSlotGoToBooker: "Modifier le réservant",
    regSlotClear: "Supprimer",
    regSlotClearConfirmTitle: "Supprimer ce voyageur ?",
    regSlotClearConfirmDesc:
      "Toutes les informations de ce voyageur seront effacées. Action irréversible.",
    regSlotSave: "Enregistrer",
    regSlotSaving: "Enregistrement...",
    regSlotCancel: "Annuler",
    regBookerAlreadyClaimedBy: (slot) =>
      `Le réservant est déjà associé au voyageur ${slot}.`,
    regSlotAutoSwitched: (from, to) =>
      `Quelqu'un vient de compléter le voyageur ${from}. Nous vous amenons au voyageur ${to}.`,
    regSlotAutoSwitchedAllFull: (slot) =>
      `Quelqu'un vient de compléter le voyageur ${slot}. Tout est terminé.`,
    regErrorTitle: "Vérifiez les informations",
    regErrorGeneric: "Impossible d'enregistrer. Réessayez.",
    regCompletedTitle: "Enregistrement terminé !",
    regCompletedDesc:
      "Merci. Tout est prêt. Vous verrez les boutons d'ouverture dès le début de votre séjour.",
    regLockedDesc:
      "Vos informations ont été envoyées au registre obligatoire. Si vous devez modifier quelque chose, contactez l'hôte.",
    regEditMyData: "Modifier mes informations",
    regGuestEditLocked:
      "Le séjour a déjà commencé. Contactez l'hôte si vous devez modifier quelque chose.",
    regOpenAfterRegistration:
      "Veuillez terminer l'enregistrement pour pouvoir ouvrir les portes.",
    regBookingReferenceShort: "Référence",

    fieldNombre: "Prénom",
    fieldPrimerApellido: "Nom",
    fieldSegundoApellido: "Deuxième nom",
    fieldSegundoApellidoHint: "Facultatif si vous n'en avez pas",
    fieldFechaNacimiento: "Date de naissance",
    fieldNacionalidad: "Nationalité",
    fieldNacionalidadSearch: "Rechercher un pays",
    fieldNacionalidadNoResults: "Aucun résultat",
    fieldSexo: "Sexe",
    sexoHombre: "Homme",
    sexoMujer: "Femme",
    sexoOtro: "Autre",
    fieldDocumentoTipo: "Type de document",
    docTipoNIF: "DNI / NIF",
    docTipoNIE: "NIE",
    docTipoPasaporte: "Passeport",
    docTipoOtro: "Autre document étranger",
    fieldDocumentoNumero: "Numéro de document",
    docScanCta: "Scanner le document",
    docScanHint: "Prenez une photo et nous remplissons les champs pour vous.",
    docScanProcessing: "Lecture du document...",
    docScanSuccess: "Données extraites. Vérifiez et corrigez si besoin.",
    docScanRetake: "Changer la photo",
    docScanRemove: "Retirer la photo",
    docScanPrivacy:
      "Nous utilisons la photo uniquement pour remplir les champs. Elle n'est conservée nulle part.",
    docScanDiscarded: "Photo supprimée. Nous ne conservons que les données.",
    docScanAiBadge: "IA",
    docScanAiTooltip: "Traité par IA",
    docScanErrorNoDocument:
      "Nous n'avons pas reconnu de document. Essayez avec une autre photo ou saisissez-le à la main.",
    docScanErrorUpstream:
      "Nous n'avons pas pu traiter la photo. Réessayez ou saisissez-le à la main.",
    personalSectionLabel: "Informations personnelles",
    reviewAiPrefillTitle: "Vérifiez ces informations",
    reviewAiPrefillDesc:
      "Nous les avons remplies à partir de votre document. Vérifiez qu'elles sont correctes et modifiez ce qu'il faut.",
    docEnterManually: "Je préfère le saisir à la main",
    docHideManual: "Scanner à la place",
    docSectionLabel: "Pièce d'identité",
    fieldContacto: "Contact",
    fieldContactoHint: "Indiquez au moins un téléphone ou un email.",
    fieldTelefono: "Téléphone",
    fieldCorreo: "Email",

    errRequired: "Champ obligatoire",
    errTooLong: "Trop long",
    errInvalidDate: "Date invalide",
    errInvalidCountry: "Choisissez un pays dans la liste",
    errInvalidNIF: "DNI/NIF invalide",
    errInvalidNIE: "NIE invalide",
    errInvalidPasaporte: "Passeport invalide",
    errInvalidDocFormat: "Document invalide",
    errInvalidPhone: "Téléphone invalide",
    errInvalidEmail: "Email invalide",
    errContactRequired: "Indiquez au moins un téléphone ou un email",
    errBookerMissing: "Complétez d'abord les informations du réservant",
    errBookerAlreadyClaimed: "Un autre voyageur est déjà associé au réservant",
    errStayStarted: "Le séjour a déjà commencé",
    errNotRegistered: "Terminez l'enregistrement pour ouvrir les portes",

    // Admin section: only the dictionary type requires these; admin UI is
    // English-only and never reads the FR strings.
    adminStayGuestCountLabel: "Nombre de voyageurs",
    adminStayGuestCountHelp: "Personnes qui séjournent (1, 2 ou 3).",
    adminBookingReferenceLabel: "Référence",
    adminBookingReferenceHelp:
      "Numéro de réservation Booking. Non affiché au voyageur.",
    adminRegistrationTitle: "Enregistrement des voyageurs",
    adminRegistrationBooker: "Réservant",
    adminRegistrationGuest: (n) => `Voyageur ${n}`,
    adminRegistrationEmptySlot: "À compléter",
    adminRegistrationLastUpdated: (when) => `Mis à jour ${when}`,
    adminRegistrationEdit: "Modifier",
    adminRegistrationClear: "Vider",
    adminRegistrationStatusComplete: "Enregistrement terminé",
    adminRegistrationStatusIncomplete: (filled, total) =>
      `${filled} sur ${total} complétés`,
    adminRegistrationMirror: "Mêmes données que le réservant",
    adminRegistrationGuestCount: (count) =>
      count === 1 ? "1 voyageur" : `${count} voyageurs`,
    adminRegistrationBookingReference: "Référence",
    adminRegistrationNoBookingReference: "Sans référence",
  },
};

const STORAGE_KEY = "hp.lang";

function detectInitial(): Lang {
  if (typeof window === "undefined") return "es";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "es" || stored === "en" || stored === "fr") return stored;
  return "es";
}

type LangContextValue = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Dict;
};

const LanguageContext = createContext<LangContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(detectInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {}
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider
      value={{ lang, setLang, t: dictionaries[lang] }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LangContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used inside <LanguageProvider>");
  }
  return ctx;
}
